import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { login } from "@/lib/api/auth.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/login")({
  head: () => ({
    meta: [{ title: "Admin Login — KS Lau & Co" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminLoginPage,
});

function stripCredentialsFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("email") && !url.searchParams.has("password")) return;

  url.searchParams.delete("email");
  url.searchParams.delete("password");
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    stripCredentialsFromUrl();
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login({ data: { email, password } });
      if (result.ok) {
        navigate({ to: "/admin" });
      } else {
        toast.error(result.error);
      }
    } catch {
      toast.error("Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <form
        method="post"
        action="/admin/login"
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border bg-background p-8 shadow-sm"
      >
        <div>
          <h1 className="text-xl font-semibold text-primary">KSLAU CMS</h1>
          <p className="text-sm text-muted-foreground">Sign in to manage content</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </div>
  );
}
